import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Toast, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { Goal } from '@/components/liSettings/BudgetAndGoals/Goal/Goal.tsx';
import { GoalBudget } from '@/components/liSettings/BudgetAndGoals/GoalBudget/GoalBudget.tsx';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { getScreensVolumetryAndForecast } from '@/services/common.service.ts';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { type LineItemStore, useStore as useLineItemStore } from '@/store/local/lineItem';

import './budgetAndGoals.styles.scss';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';

import { SonataMORE } from '@/components/common/SonataMORE';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

type State = {
  showErrorToast: boolean;
};

export function BudgetAndGoals() {
  const { formatMessage } = useIntl();

  const user = useGlobalStore(getUser);
  const userJSON = user.toJSON();
  const userRoles = useGlobalStore(getUserRoles);
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const {
    optimizationModel,
    countries: countriesState,
    setOptimizationModel,
    type: mediaChannelSelected
  } = useLineItemStore() as LineItemStore;
  const { beginDate, countries, endDate, errors, getScreens, goals } = useLineItemFormContext();
  const isDooh = mediaChannelSelected && isDOOHCampaign(mediaChannelSelected);
  const lineItemScreens = getScreens();
  const deviceIds = lineItemScreens?.map(screen => screen.device_id) || [];
  const screensInventoryCount = useRef<number | null>(null);
  const country = countries?.[0].toUpperCase() || '';
  const overallGoal = goals?.find(({ timeFrame, type }) => timeFrame === 'overall' && type !== 'budget');
  const dailyGoal = goals?.find(({ timeFrame, type }) => timeFrame === 'daily' && type !== 'budget');
  const [{ showErrorToast }, setState] = useState<State>({ showErrorToast: true });

  const error = errors?.goals ? errors.goals : undefined;

  const { data: forecastingData } = useQueryFetch(
    ['volumetry_and_forecast', country, beginDate, endDate],
    () => {
      screensInventoryCount.current = lineItemScreens?.length || 0;
      const sourceElementIds = lineItemScreens?.filter(s => s._id).map(s => s._id);
      return getScreensVolumetryAndForecast({
        beginDate: beginDate!.toISOString()!,
        country,
        deviceIds,
        endDate: endDate!.toISOString()!,
        sourceElementIds: sourceElementIds!.length > 0 ? sourceElementIds : undefined
      });
    },
    {
      enabled: !!beginDate && !!endDate && !!lineItemScreens?.length && !screensInventoryCount.current,
      keepPreviousData: true
    }
  );

  const handleToastClose = () => setState(prevState => ({ ...prevState, showErrorToast: false }));

  return (
    <div className="budgetAndGoals" id={'budgetAndGoals-box'}>
      <header className="budgetAndGoals__header">
        <div className="budgetAndGoals__title">
          {formatMessage({
            defaultMessage: 'Budget & Goals',
            id: 'budgetGoals.title'
          })}
        </div>
        <div className="budgetAndGoals__subtitle">
          {formatMessage({
            defaultMessage:
              'Set your campaign budget and goals. Your ads will stop showing when the goal or budget has been reached.',
            id: 'budgetGoals.subtitle'
          })}
        </div>
      </header>
      <div className="budgetAndGoals__body">
        {showErrorToast && error?.message && (
          <Toast
            autoClose={false}
            className="settings__errorToast"
            closeToast={handleToastClose}
            icon="fal fa-exclamation-triangle"
            text={error?.message}
            type="danger"
          />
        )}
        <div className="budgetAndGoals__firstLine">
          <GoalBudget className="overallBudgetGoal" goalTimeFrame="overall" />
          {checkUserResourcePermissions('campaignDailyBudget', user.toJSON(), userRoles) && (
            <GoalBudget className="dailyBudgetGoal" goalTimeFrame="daily" />
          )}
        </div>
        <div className="budgetAndGoals__section">
          <div className="budgetAndGoals__goalsBudgetContainer">
            <Goal
              goal={
                overallGoal || {
                  target: null,
                  type: 'impressions'
                }
              }
              limitExceededMessage={formatMessage({
                defaultMessage: 'This target may not be reached',
                id: 'budgetGoals.limitExceeded'
              })}
              limits={forecastingData}
              timeFrame="overall"
            />
            {checkUserResourcePermissions('campaignDailyBudget', user.toJSON(), userRoles) && (
              <Goal
                goal={
                  dailyGoal || {
                    target: null,
                    type: 'impressions'
                  }
                }
                timeFrame="daily"
              />
            )}
          </div>
        </div>
      </div>
      {!isDooh && (
        <SonataMORE
          campaignCountries={countriesState}
          campaignOptimizationModel={optimizationModel}
          currencyInfo={baseCurrency}
          goals={goals.map(g => ({
            target: g.target ?? undefined,
            timeFrame: g.timeFrame,
            type: g.type
          }))}
          setCampaignOptimizationModel={setOptimizationModel}
          user={userJSON}
          userRoles={userRoles}
        />
      )}
    </div>
  );
}
